/**
 * EL ANÁLISIS DE LAS FOTOS, ADELANTADO (Pablo, 28-09-2026: «la IA no puede tardar tanto;
 * que se analice mientras lo mandas y, mientras te lleva a la sesión, que la IA analice»).
 *
 * Antes la IA solo arrancaba cuando alguien ABRÍA el parte, y tardaba ~40 s (en la demo:
 * enviado 06:54:26, analizado 06:55:06). Ahora:
 *   1. el formulario la pide en cuanto las fotos están listas (`adelantarAnalisis`), antes
 *      de que exista el parte: se guarda por la huella de las fotos, sin parte;
 *   2. al enviar, el parte ya existe y se pregunta otra vez por las MISMAS fotos
 *      (`analizarParte`): la función lo encuentra hecho (o espera a que acabe) y lo ata al
 *      parte, para que se borre con él; el resultado queda también en el propio parte;
 *   3. la ficha del parte, si se abre mientras tanto, espera a ESE análisis
 *      (`analisisEnMarcha`) en lugar de pedir otro y pagarlo dos veces.
 *
 * La huella de la función es de los BYTES: el formulario sube el mismo fichero comprimido
 * que manda a analizar, y la ficha descarga ese fichero, así que la huella coincide.
 */
import { supabase } from "@/integrations/supabase/client";
import { fileToBase64 } from "@/lib/imageCompression";
import { candidatosDe, limpiarNombrePlaga } from "@/lib/pestDiagnosis";

export interface AiAnalysisData {
  pestName?: string;
  scientificName?: string;
  severity?: string;
  description?: string;
  confidence?: number;
  language?: string;
  analyzed_at?: string;
  /** false = se analizó y NO se reconoció nada. Se guarda igual para no repetir
   *  la llamada cada vez que se abre la ficha. */
  identified?: boolean;
  /** true = los dos modelos coincidieron; false = discreparon; undefined = un solo
   *  modelo (administrador) o análisis antiguo sin este dato. */
  agreementConfirmed?: boolean;
  /** Qué más podría ser (lo que dijo el otro modelo y las alternativas más
   *  plausibles). Se enseña cuando el diagnóstico es solo una sospecha. */
  candidates?: string[];
  /** true = solo contestó el modelo barato sin venir seguro (Astra falló): el veredicto
   *  vale para ENSEÑARLO ahora, pero NO es definitivo y no se guarda en el parte
   *  (invariante A13; 01-10-2026, plan cultivos SIEX, F0). */
  provisional?: boolean;
  /* ── IA guiada por cultivo (05-10-2026, plan cultivos SIEX F7). Solo llegan cuando la
     función analizó CON la lista del cultivo; sin ella, no aparecen y todo es como antes. */
  /** La plaga de la EMPRESA que resolvió el servidor (nunca la escribe el modelo). */
  pestId?: string | null;
  /** pest = una plaga · beneficial = un auxiliar · abiotic = fisiopatía/carencia ·
   *  healthy = sana · unclear = la foto no deja saberlo. */
  outcome?: "pest" | "beneficial" | "abiotic" | "healthy" | "unclear";
  /** Lo que dijo no está en la lista del cultivo. */
  outsideList?: boolean;
  /** De qué parte de la lista salió (lista del cultivo, confirmada aquí, vigilancia, empresa, catálogo). */
  listSource?: string | null;
  /** El cultivo con que se analizó (código SIEX), si se sabía. */
  cropCode?: number | null;
  /** none = prompt abierto · guided · strict. */
  candidateMode?: "none" | "guided" | "strict";
}

/** Lo de F7 que trae la respuesta, ya saneado (vacío si se analizó sin lista). */
function camposDeLista(data: Record<string, unknown> | null | undefined): Partial<AiAnalysisData> {
  const out: Partial<AiAnalysisData> = {};
  const meta = (data?.candidates ?? null) as { mode?: unknown; cropCode?: unknown } | null;
  const modo = meta?.mode;
  if (modo === "none" || modo === "guided" || modo === "strict") out.candidateMode = modo;
  if (typeof meta?.cropCode === "number") out.cropCode = meta.cropCode;
  const d = (data?.data ?? null) as Record<string, unknown> | null;
  const outcome = (d?.outcome ?? data?.outcome) as unknown;
  if (outcome === "pest" || outcome === "beneficial" || outcome === "abiotic" || outcome === "healthy" || outcome === "unclear") out.outcome = outcome;
  if (d && typeof d.pestId === "string") out.pestId = d.pestId;
  if (d && typeof d.outsideList === "boolean") out.outsideList = d.outsideList;
  if (d && typeof d.listSource === "string") out.listSource = d.listSource;
  return out;
}

/**
 * La función contestó, pero ningún modelo dio un veredicto (`success: false`): es un
 * FALLO, no un «no se ha identificado nada». Antes se convertía en `identified: false`
 * y se guardaba en el parte, así que un corte de créditos o un modelo caído quedaba
 * para siempre como «No se reconoció ninguna plaga» y la ficha ya no volvía a
 * preguntar (01-10-2026, plan cultivos SIEX, F0). Ahora se lanza, y quien llama lo
 * trata como cualquier otro fallo: sin guardar y con «Reintentar».
 */
export class AnalisisSinVeredicto extends Error {
  constructor() {
    super("Ningún modelo dio un veredicto sobre las fotos");
    this.name = "AnalisisSinVeredicto";
  }
}

/** ¿Se puede guardar en el parte como resultado definitivo? Solo lo que no es provisional
 *  (A13: un fallo o un veredicto provisional nunca se guardan). */
export const sePuedeGuardarEnElParte = (a: AiAnalysisData): boolean => a.provisional !== true;

/** Idioma del análisis: los cuatro que sabe la función; el resto, español. */
export function idiomaDelAnalisis(lang: string | null | undefined): string {
  const raw = (lang || "es").toString().toLowerCase().split("-")[0];
  return ["en", "es", "it", "ar"].includes(raw) ? raw : "es";
}

/** Tope de fotos por llamada (el de la función). */
export const MAX_FOTOS_ANALISIS = 6;

/**
 * Una llamada a la función. Lanza si falla de verdad (red, 4xx…, o ningún modelo
 * contestó: `AnalisisSinVeredicto`): eso NO es un veredicto y tiene que poder
 * reintentarse. «No reconoció nada» sí lo es, y vuelve como tal. Lo provisional vuelve
 * marcado (`provisional: true`) para que nadie lo guarde como definitivo.
 */
export async function pedirAnalisis(opts: {
  base64s: string[];
  lang: string;
  blockId: string | null;
  sessionId: string | null;
  force?: boolean;
  /** Sin sector: la ubicación, si se sabe (F7: la lista sale de sus cultivos). */
  locationId?: string | null;
  /** Sin sector: el cultivo elegido a mano (chip «Cultivo» del escáner; F7). */
  cropCode?: number | null;
}): Promise<AiAnalysisData> {
  const { base64s, lang, blockId, sessionId, force = false, locationId = null, cropCode = null } = opts;
  // Se manda `images` Y `image`. La función nueva usa el array; la vieja, que solo
  // conoce `image`, sigue funcionando con la primera foto. `location_id` y `crop_code`
  // solo cuando hay algo que decir (una función vieja los ignora).
  const body: Record<string, unknown> = { images: base64s, image: base64s[0], language: lang, block_id: blockId, session_id: sessionId, force };
  if (locationId) body.location_id = locationId;
  if (cropCode != null) body.crop_code = cropCode;
  const { data, error } = await supabase.functions.invoke("analyze-pest-image", { body });
  if (error) throw error;
  // Sin veredicto de ningún modelo: fallo reintentable, nunca «no hay nada».
  if (!data?.success) throw new AnalisisSinVeredicto();
  const provisional = data.provisional === true ? { provisional: true as const } : {};
  const lista = camposDeLista(data);
  if (!data.identified || !data.data) {
    // La IA SÍ contestó y no reconoció nada: eso es un veredicto (si no es provisional).
    return { identified: false, language: lang, analyzed_at: new Date().toISOString(), ...provisional, ...lista };
  }
  const d = data.data;
  return {
    ...provisional,
    ...lista,
    // Un nombre, sin la duda dentro: la duda ya la dice la confianza.
    pestName: limpiarNombrePlaga(d.pestName),
    candidates: candidatosDe({ otros: data?.agreement?.alternatives, consideradas: d.alternatives, principal: d.pestName }),
    scientificName: d.scientificName,
    severity: d.severity,
    description: d.description,
    confidence: typeof d.confidence === "number" ? d.confidence : undefined,
    language: lang,
    analyzed_at: new Date().toISOString(),
    identified: true,
    // Solo hay acuerdo que mostrar cuando han contestado dos modelos.
    agreementConfirmed: data?.agreement && data.agreement.answered > 1 ? data.agreement.confirmed === true : undefined,
  };
}

/** Qué fotos son, para saber si lo adelantado sigue valiendo al enviar. */
export function firmaDeFotos(files: Blob[]): string {
  return files.map((f) => `${(f as File).name ?? ""}:${f.size}:${(f as File).lastModified ?? ""}`).join("|");
}

type Adelantado = { firma: string; base64s: Promise<string[]>; promesa: Promise<AiAnalysisData>; desde: number };

/** Lo pedido desde el formulario, por el id que tendrá el parte. */
const adelantados = new Map<string, Adelantado>();
/** Lo que está en marcha para un parte que YA existe (lo espera su ficha), y desde cuándo. */
const enMarcha = new Map<string, { promesa: Promise<AiAnalysisData>; desde: number }>();

const aBase64 = (files: Blob[]) => Promise.all(files.slice(0, MAX_FOTOS_ANALISIS).map((f) => fileToBase64(f)));

/**
 * Desde el formulario, con el parte aún sin crear: arranca el análisis de estas fotos.
 * Si ya se pidió para las mismas fotos, no hace nada. Nunca lanza.
 */
export function adelantarAnalisis(futuroParteId: string, files: Blob[], opts: { lang: string; blockId: string | null; locationId?: string | null }) {
  if (files.length === 0) return;
  const firma = firmaDeFotos(files);
  if (adelantados.get(futuroParteId)?.firma === firma) return;
  const base64s = aBase64(files);
  const promesa = base64s.then((b) => pedirAnalisis({ base64s: b, lang: opts.lang, blockId: opts.blockId, sessionId: null, locationId: opts.locationId ?? null }));
  promesa.catch(() => undefined);
  adelantados.set(futuroParteId, { firma, base64s, promesa, desde: Date.now() });
}

/**
 * Al enviar, con el parte ya creado: el análisis de SUS fotos, atado a él, y guardado en
 * el parte. Si se adelantó con las mismas fotos, se espera a ese y se ata (la función lo
 * encuentra hecho: no se paga otra vez). Nunca lanza; devuelve la promesa por si alguien
 * quiere esperarla.
 */
export function analizarParte(parteId: string, files: Blob[], opts: { lang: string; blockId: string | null }): Promise<AiAnalysisData> | null {
  if (files.length === 0) return null;
  const firma = firmaDeFotos(files);
  const previo = adelantados.get(parteId);
  adelantados.delete(parteId);
  const valePrevio = previo && previo.firma === firma;
  const base64s = valePrevio ? previo.base64s : aBase64(files);
  const promesa = (async () => {
    // Lo adelantado, si acabó bien o mal, da igual: la segunda pregunta ya lleva el parte.
    if (valePrevio) await previo.promesa.catch(() => undefined);
    // Si falla (red, 4xx, `AnalisisSinVeredicto`) lanza aquí y NO se escribe nada en el parte.
    const resultado = await pedirAnalisis({ base64s: await base64s, lang: opts.lang, blockId: opts.blockId, sessionId: parteId });
    // Provisional: se devuelve para enseñarlo, pero no se guarda como definitivo; la
    // próxima apertura de la ficha volverá a preguntar (A13; 01-10-2026, F0).
    if (!sePuedeGuardarEnElParte(resultado)) return resultado;
    // En el parte también: así la lista, el panel y la ficha lo tienen sin llamar a nadie.
    await supabase
      .from("pest_scouting_sessions")
      .update({ ai_analysis: resultado } as never)
      .eq("id", parteId)
      .then(({ error }) => { if (error) console.warn("ai_analysis no guardado en el parte:", error.message); });
    return resultado;
  })();
  // La barra de la ficha cuenta desde que se PIDIÓ (en el formulario, si se adelantó).
  enMarcha.set(parteId, { promesa, desde: valePrevio ? previo.desde : Date.now() });
  promesa.catch((err) => console.warn("análisis del parte falló:", err)).finally(() => {
    if (enMarcha.get(parteId)?.promesa === promesa) enMarcha.delete(parteId);
  });
  return promesa;
}

/** El análisis que ya va de camino para este parte, si lo hay (lo usa su ficha). */
export function analisisEnMarcha(parteId: string): Promise<AiAnalysisData> | null {
  return enMarcha.get(parteId)?.promesa ?? null;
}

/** Desde cuándo se está analizando ese parte (ms), si hay algo en marcha. */
export function inicioDelAnalisis(parteId: string): number | null {
  return enMarcha.get(parteId)?.desde ?? null;
}
